import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { DataGridPro, GridRow } from "@mui/x-data-grid-pro";
import TableSkeleton from "../../../../../components/loader/tableLoader.jsx";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import moment from "moment";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import { NumberFormat } from "../../../../../utils/numberFormat";
import UpdateInvoice from "./createInvoice.jsx";
import {
  deleteInvoice,
  sendInvoiceAlert,
  voidInvoice,
} from "../../../../../redux/agents/agentRosterDetail/agentTabs";
import { useDispatch, useSelector } from "react-redux";
import ResponseAlert from "../../../../../components/responseAlert";
import DeleteDraft from "./deleteDraft.jsx";
import VoidInvoice from "./voidInvoice.jsx";
import ConfirmModal from "../../../../../components/globalModal/ConfirmModal";
import { CustomTooltip } from "../../../../../components/shared/tooltip";
import { styled } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ViewInvoice from "./viewInvoice.jsx";
import { TablePagination } from "@mui/material";
import { getPayments } from "../../../../../redux/agents/agentRosterDetail/agentTabs";
import axios from "axios";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";

const StyledBox = styled(Box)(({ theme }) => ({
  "& .css-1pqpq69-MuiButtonGroup-root": {
    boxShadow: "none !important",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: "70vh",
}));

const InvoicesTable = (props) => {
  const { classes, filters, setFilters, id } = props;
  const [expandedRows, setExpandedRows] = useState({});
  const [invoiceData, setInvoiceData] = useState({});
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [errorAlert, setErrorAlert] = useState({
    errorMsg: "",
    errorType: "",
    isOpen: false,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isViewInvoiceOpen, setIsViewInvoiceOpen] = useState(false);
  const [selectedInvoicePdf, setSelectedInvoicePdf] = useState(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);
  const [allPayments, setAllPayments] = useState([]);
  const [isLoadingAllPayments, setIsLoadingAllPayments] = useState(false);
  const [totalPaymentsAmount, setTotalPaymentsAmount] = useState(0);
  const [hasFetchedPayments, setHasFetchedPayments] = useState(false);
  const [isTotalLoading, setIsTotalLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  // Remove invoiceMenu and local paginationModel state for optimization
  // Pagination now fully managed with parent filters

  const dispatch = useDispatch();
  const invoices = useSelector((state) => state.agentRoster.AgentTabs.invoices);
  const updateInvoice = useSelector(
    (state) => state.agentRoster.AgentTabs.updateInvoice
  );
  const delInvoice = useSelector(
    (state) => state.agentRoster.AgentTabs.delInvoice
  );
  const invoiceAlert = useSelector(
    (state) => state.agentRoster.AgentTabs.invoiceAlert
  );

  // Add a ref to store the total
  const totalRef = useRef(null);

  // Add a ref to track the last fetch time
  const lastFetchTimeRef = useRef(null);

  // Get payments data at component level
  const payments = useSelector((state) => state.agentRoster.AgentTabs.payments);

  // Create a lookup map for quick access to payment amounts by ID
  const paymentAmountsById = useMemo(() => {
    const map = {};
    if (payments?.data) {
      payments.data.forEach((payment) => {
        map[payment.id] = payment.amount;
      });
    }
    return map;
  }, [payments]);

  const handleToggleExpand = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // --- (Rest of the DataGrid columns and logic unchanged) ---

  const columns = [
    {
      field: "status",
      headerName: "Status",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              background:
                row.status === "outstanding"
                  ? "#FFECEC"
                  : row.status === "draft"
                  ? "black"
                  : row.status === "paid"
                  ? "#CDF9D8"
                  : "#d8d8d8",
              color:
                row.status === "outstanding"
                  ? "#CC0000"
                  : row.status === "draft"
                  ? "white"
                  : row.status === "paid"
                  ? "#005214"
                  : "",
              padding: "12px 16px",
              lineHeight: "1.7",
              height: "auto",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
              "& span": {
                transform: "translateY(1px)",
              },
            }}
            className={classes.statusChip}
          >
            <span>{row.status}</span>
          </Box>
        );
      },
      width: 120,
      editable: false,
      sortable: true,
    },
    {
      field: "invoice_date",
      headerName: "Date",
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {moment(row?.invoice_date).format("L")}
          </Typography>
        );
      },
      width: 120,
      editable: false,
      sortable: true,
    },
    {
      field: "invoice",
      headerName: "INVOICE #",
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: "10px",
              alignItems: "center ",
              color: "#696969",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {row?.invoice_number}
            </Typography>
            <IconButton
              onClick={() => {
                setSelectedInvoicePdf(row.pdf_path || row.pdf_url);
                setSelectedInvoiceNumber(row.invoice_number);
                setIsViewInvoiceOpen(true);
              }}
              size="small"
            >
              <DescriptionRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
      width: 150,
      editable: false,
      sortable: true,
    },
    {
      field: "category",
      headerName: "Category",
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
            {row?.category}
          </Typography>
        );
      },
      width: 180,
      editable: false,
      sortable: true,
    },
    {
      field: "description",
      headerName: "Description",
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: "14px" }}>{row?.description}</Typography>
        );
      },
      minWidth: 250,
      flex: 1,
      editable: false,
      sortable: true,
    },
    {
      field: "duedate",
      headerName: "Due Date",
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: "14px" }}>
            {moment(row?.due_date).format("L")}
          </Typography>
        );
      },
      width: 120,
      editable: false,
      sortable: true,
    },
    {
      field: "amount_due",
      headerName: "AMT DUE",
      renderCell: ({ row }) => {
        const totalPayments =
          row.payments?.reduce((sum, paymentRow) => {
            if (paymentRow?.payment && !paymentRow?.payment?.deleted_at) {
              return sum + Number(paymentRow?.amount_paid || 0);
            }
            return sum;
          }, 0) || 0;

        const amountDue = Number(row?.amount_due || 0) - totalPayments;
        const formattedAmount =
          amountDue === 0
            ? "$0.00"
            : NumberFormat({
                number: amountDue,
                maximumFractionDigits: 2,
                currency: "USD",
              });

        return <Box>{formattedAmount}</Box>;
      },
      minWidth: 100,
      editable: false,
      sortable: true,
    },
    {
      field: "invoice_amount",
      headerName: "INV AMT",
      renderCell: ({ row }) => {
        const totalAmount =
          row.charges?.reduce(
            (sum, charge) => sum + Number(charge?.amount || 0),
            0
          ) || 0;
        return (
          <Box>
            {NumberFormat({
              number: totalAmount,
              maximumFractionDigits: 2,
              currency: "USD",
            })}
          </Box>
        );
      },
      minWidth: 100,
      editable: false,
      sortable: true,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      width: 100,
      sortable: false,
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            justifyContent: "center",
            alignItems: "center",
            gap: "0px",
            position: "relative",
            display: "flex",
            minWidth: 0,
            "& .MuiIconButton-root": {
              padding: "4px",
            },
          }}
        >
          {expandedRows[params.row.id] ? (
            <IconButton
              onClick={() => handleToggleExpand(params.row.id)}
              size="small"
            >
              <ExpandLessRoundedIcon />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => handleToggleExpand(params.row.id)}
              size="small"
            >
              <ExpandMoreRoundedIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      field: "menuActions",
      headerName: "",
      width: 10,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setInvoiceData(params.row);
            setMenuPosition({ top: e.clientY, left: e.clientX });
            setAnchorEl(e.currentTarget);
          }}
          disabled={
            params.row.status === "Paid" ||
            params.row.status === "paid" ||
            params.row.status === "voided"
          }
          size="small"
          sx={{
            padding: "2px",
            ml: -2.5,
            minWidth: "12px",
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Charges and Payments columns unchanged for brevity...
  const chargesColumns = [
    {
      field: "quantity",
      headerName: "Qty",
      flex: 0.08,
      minWidth: 60,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.2,
      minWidth: 120,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.42,
      minWidth: 200,
    },
    {
      field: "attachments",
      headerName: "Attachments",
      flex: 0.1,
      minWidth: 80,
      renderCell: (params) => {
        const hasAttachments = params.row.charge_attachments?.length > 0;

        if (hasAttachments) {
          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                const attachmentUrl =
                  params.row.charge_attachments[0].file_path;
                if (attachmentUrl) {
                  setSelectedAttachment(attachmentUrl);
                  setIsAttachmentModalOpen(true);
                }
              }}
              size="small"
              sx={{
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          );
        }
        return null;
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.2,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ width: "100%", pl: 3, color: "#D32F2F" }}>
          {params.value ? `$${Number(params.value).toFixed(2)}` : "$0.00"}
        </Box>
      ),
      renderHeader: (params) => (
        <Box sx={{ pl: 3, fontWeight: 500 }}>Amount</Box>
      ),
    },
  ];

  const paymentsColumns = [
    {
      field: "date",
      headerName: "Date",
      width: 100,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "14px" }}>
          {moment(row?.payment?.payment_date).format("L")}
        </Typography>
      ),
      editable: false,
      sortable: true,
    },
    {
      field: "category",
      headerName: "Method",
      width: 170,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "14px" }}>{row?.payment?.type}</Typography>
      ),
      editable: false,
      sortable: true,
    },
    {
      field: "Description",
      headerName: "Description",
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <CustomTooltip title={row?.payment?.description} placement="top">
          <Typography sx={{ fontSize: "14px" }}>
            {row?.payment?.description}
          </Typography>
        </CustomTooltip>
      ),
      editable: false,
      sortable: true,
    },
    {
      field: "Amount",
      headerName: "AMOUNT",
      renderCell: ({ row }) => {
        if (!row?.payment || !row?.amount_paid) return null;
        const paymentId = row.payment.id;
        const paymentWithStatus = payments?.data?.find(
          (p) => p.id === paymentId
        );
        const isPosted = paymentWithStatus?.status === "Posted";
        const textColor = isPosted ? "#4CAF50" : "#696969";
        const formattedAmount = `$${Number(row.amount_paid).toFixed(2)}`;
        return (
          <Box align="center">
            {isPosted ? (
              <Typography sx={{ fontSize: "14px", color: textColor }}>
                {formattedAmount}
              </Typography>
            ) : (
              <CustomTooltip title="Pending" placement="top">
                <Typography sx={{ fontSize: "14px", color: textColor }}>
                  {formattedAmount}
                </Typography>
              </CustomTooltip>
            )}
          </Box>
        );
      },
      width: 120,
      editable: false,
      sortable: true,
    },
  ];

  // Deduplicate and client filtering zeroed out when server is handling pagination
  const filteredData = useMemo(() => {
    if (!invoices?.data?.length) {
      return [];
    }
    // Only filter for search if client-side, otherwise return raw data (pagination and search managed by parent/api)
    if (!filters.search) {
      return invoices.data;
    }
    const searchTerm = filters.search.toLowerCase().trim();

    return invoices.data.filter((invoice) => {
      if (invoice.invoice_number?.toLowerCase().includes(searchTerm)) {
        return true;
      }
      if (
        moment(invoice.invoice_date)
          .format("L")
          .toLowerCase()
          .includes(searchTerm)
      ) {
        return true;
      }
      if (invoice.description?.toLowerCase().includes(searchTerm)) {
        return true;
      }
      const amountStr = invoice.amount_due?.toString().toLowerCase() || "";
      if (amountStr.includes(searchTerm)) {
        return true;
      }
      return false;
    });
  }, [filters.search, invoices?.data]);

  // Payments fetching unchanged for brevity...

  // Remove local paginationModel. Use filters from parent.
  // On page/pageSize change, update filters only, NOT local state!

  // TablePagination will use filters.pageNumber, filters.pageSize directly
  // UX: internal TablePagination is 0-based, back-end filter is 1-based.

  // Get current page and rows per page from filters prop (with fallback)
  const page = Math.max((filters.pageNumber || 1) - 1, 0);
  const rowsPerPage = filters.pageSize || 25;
  const totalCount = invoices?.count || 0;

  // The expanded content, attachment modal, and dialogs are unchanged from before

  const getExpandedContentHeight = (charges, payments) => {
    const chargesLength = charges?.length || 0;
    const paymentsLength =
      payments?.filter((p) => p?.payment && !p?.payment?.deleted_at)?.length ||
      0;

    const rowHeight = 40; // Match the CSS maxHeight/minHeight
    const headerHeight = 56;
    const containerPadding = 80; // Increased from 64 to 80
    const rowSpacing = 4; // Account for the margin in CSS (2px top + 2px bottom)

    const maxRows = Math.max(chargesLength, paymentsLength);
    const rowsHeight = maxRows * rowHeight + (maxRows - 1) * rowSpacing;

    return rowsHeight + headerHeight + containerPadding;
  };

  const AttachmentModal = () => (
    <Dialog
      open={isAttachmentModalOpen}
      onClose={() => setIsAttachmentModalOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Attachment</Typography>
          <IconButton
            onClick={() => setIsAttachmentModalOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedAttachment && (
          <Box
            component="iframe"
            src={selectedAttachment}
            width="100%"
            height="calc(90vh - 100px)"
            sx={{ border: "none" }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  // Dialog handling functions...
  const handleSuccess = () => {
    setOpenConfirm(false);
    setErrorAlert({
      errorMsg: "You have successfully updated the invoice",
      errorType: "success",
      isOpen: true,
    });
  };

  const handleError = (error) => {
    setErrorAlert({
      errorMsg: JSON.stringify(error),
      errorType: "warning",
      isOpen: true,
    });
  };

  const handleConfirmVoid = ({ reason, sendAlert }) => {
    dispatch(
      voidInvoice({
        invoice_id: invoiceData?.id,
        schema: {
          status: "voided",
          user_id: id,
          reason_for_voiding: reason,
          email_alert: sendAlert,
        },
        handleSuccess: () => {
          setOpenConfirm(false);
          setErrorAlert({
            errorMsg: "Invoice voided successfully",
            errorType: "success",
            isOpen: true,
          });
        },
        handleError: (error) => {
          setErrorAlert({
            errorMsg: error,
            errorType: "error",
            isOpen: true,
          });
        },
      })
    );
  };

  const handleSuccessDel = () => {
    setDeleteConfirm(false);
    setErrorAlert({
      errorMsg: "You have successfully deleted the invoice",
      errorType: "success",
      isOpen: true,
    });
  };

  const handleConfirmDelete = () => {
    dispatch(
      deleteInvoice({
        invoice_id: invoiceData?.id,
        handleSuccess: handleSuccessDel,
        handleError,
      })
    );
  };

  const handleSuccessAlert = () => {
    setOpenAlert(false);
    setErrorAlert({
      errorMsg: "Alert sent successfully!",
      errorType: "success",
      isOpen: true,
    });
  };

  const handleConfirm = () => {
    dispatch(
      sendInvoiceAlert({
        invoice_id: invoiceData?.id,
        handleSuccess: handleSuccessAlert,
        handleError,
      })
    );
  };

  // Remove dead code and keep only what is required
  return (
    <StyledBox
      sx={{
        borderRadius: "18px",
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box
        className={classes.gridViewSection}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: "calc(100vh - 270px)",
          borderRadius: "18px !important",
          backgroundColor: "#FFFFFF !important",
          background: "#FFFFFF !important",
          position: "relative !important",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            "& .MuiDataGrid-root": {
              border: "none !important",
              flex: 1,
            },
            "& .MuiDataGrid-main": {
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
            },
            "& .MuiDataGrid-virtualScroller": {
              flex: "1 1 auto",
            },
          }}
        >
          <DataGridPro
            rows={filteredData}
            loading={invoices.isLoading}
            columns={columns}
            disableSelectionOnClick
            rowHeight={56}
            getRowHeight={(params) => {
              if (expandedRows[params.id] && params.row) {
                return getExpandedContentHeight(
                  params.row.charges || [],
                  params.row.payments || []
                );
              }
              return 56;
            }}
            sx={{
              border: "none !important",
              overflow: "visible !important",
              padding: "0 !important",
              "& .MuiDataGrid-main": {
                padding: "0 !important",
                overflow: "visible !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflow: "visible !important",
              },
              "& .MuiDataGrid-virtualScrollerContent": {
                overflow: "visible !important",
                paddingTop: "12px !important",
              },
              // ... rest of sx unchanged
            }}
            getRowClassName={(params) =>
              expandedRows[params.id] ? "expanded-row" : ""
            }
            components={{
              LoadingOverlay: TableSkeleton,
              Row: (props) => (
                <Box sx={{ margin: "5px 0 !important" }}>
                  <Box
                    className={`css-1ix50fd`}
                    sx={{
                      position: "relative",
                      overflow: "visible",
                      width: "100%",
                      margin: "0",
                      background: "white !important",
                      boxShadow: expandedRows[props.row.id]
                        ? "0px -10px 50px -10px rgba(0, 0, 0, 0.04) !important"
                        : "0px 10px 50px 0px rgba(0, 0, 0, 0.04) !important",
                      borderRadius: expandedRows[props.row.id]
                        ? "20px 20px 0 0 !important"
                        : "20px !important",
                      ...(expandedRows[props.row.id] && {
                        zIndex: 1,
                      }),
                    }}
                  >
                    <GridRow {...props} />
                    {expandedRows[props.row.id] && (
                      <>
                        <Box
                          sx={{
                            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                            margin: "0 20px",
                            position: "relative",
                            zIndex: 1,
                          }}
                        />
                        <Box
                          className="expanded-content"
                          sx={{
                            padding: "20px",
                            paddingBottom: "20px !important",
                            background: "white !important",
                            boxShadow:
                              "0px 10px 50px 0px rgba(0, 0, 0, 0.04) !important",
                            borderRadius: "0 0 20px 20px !important",
                            marginTop: "-20px !important",
                            paddingTop: "20px !important",
                            position: "relative",
                            zIndex: 0,
                            height:
                              props?.row?.charges || props?.row?.payments
                                ? `${getExpandedContentHeight(
                                    props?.row?.charges,
                                    props?.row?.payments
                                  )}px`
                                : "auto",
                          }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={6}>
                              <Typography className={classes.subHeading}>
                                Charges
                              </Typography>
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth: "95%",
                                  width: "95%",
                                  "& .MuiDataGrid-root": {
                                    width: "100%",
                                    "& .MuiDataGrid-row": {
                                      border:
                                        "1px solid rgba(0, 0, 0, 0.08) !important",
                                      borderRadius: "20px !important",
                                      margin: "2px 0 !important",
                                      background: "white !important",
                                      minHeight: "40px !important",
                                      maxHeight: "40px !important",
                                      boxShadow: "none !important",
                                      "& .MuiDataGrid-cell": {
                                        boxShadow: "none !important",
                                        minHeight: "40px !important",
                                        maxHeight: "40px !important",
                                        lineHeight: "40px !important",
                                      },
                                      "& .MuiBox-root": {
                                        boxShadow: "none !important",
                                      },
                                    },
                                    "& .css-1ix50fd": {
                                      boxShadow: "none !important",
                                    },
                                    "& .css-199kaup": {
                                      boxShadow: "none !important",
                                    },
                                  },
                                }}
                              >
                                <DataGridPro
                                  rows={
                                    props?.row?.charges?.length
                                      ? props?.row?.charges
                                      : []
                                  }
                                  columns={chargesColumns}
                                  hideFooter={true}
                                  autoHeight
                                  rowHeight={40}
                                  disableColumnMenu
                                  components={{
                                    NoRowsOverlay: () => null,
                                  }}
                                  sx={{
                                    border: "none !important",
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.subHeading}>
                                Payments
                              </Typography>
                              <Box
                                sx={{
                                  width: "100%",
                                  "& .MuiDataGrid-root": {
                                    width: "100%",
                                    "& .MuiDataGrid-row": {
                                      border:
                                        "1px solid rgba(0, 0, 0, 0.08) !important",
                                      borderRadius: "20px !important",
                                      margin: "2px 0 !important",
                                      background: "white !important",
                                      minHeight: "40px !important",
                                      maxHeight: "40px !important",
                                      boxShadow: "none !important",
                                      "& .MuiDataGrid-cell": {
                                        boxShadow: "none !important",
                                        minHeight: "40px !important",
                                        maxHeight: "40px !important",
                                        lineHeight: "40px !important",
                                      },
                                      "& .MuiBox-root": {
                                        boxShadow: "none !important",
                                      },
                                    },
                                    "& .css-1ix50fd": {
                                      boxShadow: "none !important",
                                    },
                                    "& .css-199kaup": {
                                      boxShadow: "none !important",
                                    },
                                  },
                                }}
                              >
                                <DataGridPro
                                  rows={
                                    props?.row?.payments?.filter(
                                      (pi) =>
                                        pi?.payment && !pi?.payment?.deleted_at
                                    ) || []
                                  }
                                  columns={paymentsColumns}
                                  hideFooter={true}
                                  autoHeight
                                  rowHeight={52}
                                  disableColumnMenu
                                  components={{
                                    NoRowsOverlay: () => null,
                                  }}
                                  sx={{
                                    border: "none !important",
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              ),
              ColumnSeparator: () => null,
              DetailPanelToggleCell: (props) => {
                // Get the row data
                const row = props.row;
                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div>
                      {React.createElement(
                        props.components.DetailPanelToggleCell,
                        props
                      )}
                    </div>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setInvoiceData(row);
                        setMenuPosition({ top: e.clientY, left: e.clientX });
                        setAnchorEl(e.currentTarget);
                      }}
                      disabled={
                        row.status === "Paid" ||
                        row.status === "paid" ||
                        row.status === "voided"
                      }
                      size="small"
                      sx={{ padding: 0, ml: 1 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </div>
                );
              },
            }}
            disableColumnResize={true}
            pagination={false}
            hideFooter={true}
            onCellClick={(params, event) => {
              // Only handle clicks in the first column (detail panel column)
              if (params.field === "__detail" || params.field === "detail") {
                // Check if the click was on the right side of the cell (where our menu icon is)
                const cellRect = event.currentTarget.getBoundingClientRect();
                const clickX = event.clientX;
                // If click is within 32px of the right edge of the cell
                if (clickX > cellRect.right - 32) {
                  event.stopPropagation(); // Prevent row expansion
                  setInvoiceData(params.row);
                  setMenuPosition({ top: event.clientY, left: event.clientX });
                  setAnchorEl(event.currentTarget);
                }
              }
            }}
          />
        </Box>
        <Box
          sx={{
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            padding: "8px 16px",
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "white",
            width: "100%",
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => {
              setFilters({
                ...filters,
                pageNumber: newPage + 1,
                pageSize: rowsPerPage,
              });
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setFilters({
                ...filters,
                pageNumber: 1,
                pageSize: parseInt(e.target.value, 25),
              });
            }}
            sx={{
              ".MuiTablePagination-toolbar": {
                minHeight: "40px",
                paddingLeft: "8px",
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  margin: 0,
                },
            }}
          />
        </Box>
      </Box>
      <UpdateInvoice
        open={openUpdate}
        isUpdate={true}
        setOpen={setOpenUpdate}
        invoiceData={invoiceData}
        id={id}
      />
      <VoidInvoice
        open={openConfirm}
        setOpen={setOpenConfirm}
        handleConfirm={handleConfirmVoid}
        title={`Void Invoice #${invoiceData?.invoice_number}`}
        loading={updateInvoice.isLoading}
      />
      <DeleteDraft
        open={deleteConfirm}
        setOpen={setDeleteConfirm}
        handleConfirm={handleConfirmDelete}
        title={`Delete Invoice #${invoiceData?.invoice_number}`}
        loading={delInvoice.isLoading}
      />
      <ResponseAlert
        open={errorAlert.isOpen}
        setOpen={() =>
          setErrorAlert({ errorMsg: "", errorType: "", isOpen: false })
        }
        alertType={errorAlert.errorType}
        alertMessage={errorAlert.errorMsg}
      />
      <ConfirmModal
        open={openAlert}
        setOpen={setOpenAlert}
        title="Send Confirmation"
        content="Are you sure you want to send the alert against this invoice?"
        handleConfirm={handleConfirm}
        loading={invoiceAlert.isLoading}
      />
      <Menu
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        PaperProps={{
          sx: {
            mt: 3,
            ml: -5,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
            borderRadius: "8px",
          },
        }}
      >
        {invoiceData?.status === "draft" ? (
          <>
            <MenuItem
              onClick={() => {
                setOpenUpdate(true);
                setAnchorEl(null);
              }}
            >
              Edit Draft
            </MenuItem>
            <MenuItem
              onClick={() => {
                setDeleteConfirm(true);
                setAnchorEl(null);
              }}
            >
              Delete Draft
            </MenuItem>
          </>
        ) : (
          invoiceData?.status === "outstanding" && (
            <>
              <MenuItem
                onClick={() => {
                  setOpenConfirm(true);
                  setAnchorEl(null);
                }}
              >
                Void Invoice
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenUpdate(true);
                  setAnchorEl(null);
                }}
              >
                Edit Invoice
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setOpenAlert(true);
                  setAnchorEl(null);
                }}
              >
                Send New Invoice Alert
              </MenuItem>
            </>
          )
        )}
      </Menu>
      <ViewInvoice
        open={isViewInvoiceOpen}
        setOpen={setIsViewInvoiceOpen}
        pdfUrl={selectedInvoicePdf}
        invoiceNumber={selectedInvoiceNumber}
        classes={classes}
      />
      <AttachmentModal />
    </StyledBox>
  );
};

export default InvoicesTable;
